/* eslint-disable perfectionist/sort-imports */
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import 'overlayscrollbars/overlayscrollbars.css';
import '/styles/overlayscrollbars.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import isElectron from 'is-electron';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import i18n from '/@/i18n/i18n';
import { RequestInviteModal } from '/@/renderer/features/invite/components/request-invite-modal';
import { openSettingsModal } from '/@/renderer/features/settings/utils/open-settings-modal';
import { WebAudioContext } from '/@/renderer/features/player/context/webaudio-context';
import { useCheckForUpdates } from '/@/renderer/hooks/use-check-for-updates';
import { useSyncSettingsToMain } from '/@/renderer/hooks/use-sync-settings-to-main';
import { AppRouter } from '/@/renderer/router/app-router';
import { useAuthStore, useCssSettings, useHotkeySettings, useLanguage } from '/@/renderer/store';
import { useAppTheme } from '/@/renderer/themes/use-app-theme';
import { sanitizeCss } from '/@/renderer/utils/sanitize';
import { urlConfig } from '/@/renderer/config/url-config';
import { WebAudio } from '/@/shared/types/types';
import '/@/shared/styles/global.css';
import { PlayerProvider } from '/@/renderer/features/player/context/player-context';
import { AudioPlayers } from '/@/renderer/features/player/components/audio-players';

const UpdateAvailableDialog = lazy(() =>
    import('./update-available-dialog').then((module) => ({
        default: module.UpdateAvailableDialog,
    })),
);

const ipc = isElectron() ? window.api.ipc : null;

export const App = () => {
    const { mode, theme } = useAppTheme();
    const language = useLanguage();

    const { content, enabled } = useCssSettings();
    const { bindings } = useHotkeySettings();
    const cssRef = useRef<HTMLStyleElement | null>(null);

    useSyncSettingsToMain();
    useCheckForUpdates();

    useEffect(() => {
        if (!isElectron() || !ipc) return;
        // Global handler: any main-process endpoint failure sends this event.
        // Log the user out so they can re-authenticate.
        const handler = () => {
            useAuthStore.getState().actions.setCurrentServer(null);
        };
        ipc.on('sync:session-expired', handler);
        return () => {
            ipc.removeListener('sync:session-expired', handler);
        };
    }, []);

    // The main-process watch poller refreshes the filebrowser token on expiry;
    // mirror it into the store so restarts and other sync operations stay valid.
    useEffect(() => {
        if (!isElectron() || !ipc) return;
        const handler = (_event: unknown, token: string) => {
            const server = useAuthStore.getState().currentServer;
            if (server && token) {
                useAuthStore.getState().actions.updateServer(server.id, { fbToken: token });
            }
        };
        ipc.on('sync:filebrowser-token-refreshed', handler);
        return () => {
            ipc.removeListener('sync:filebrowser-token-refreshed', handler);
        };
    }, []);

    // Auto-resume watch directory on app launch if it was active before shutdown
    useEffect(() => {
        if (!isElectron() || !ipc) return;
        const localSettings = window.api.localSettings;
        Promise.all([localSettings.get('watch_directory'), localSettings.get('watch_active')]).then(
            ([watchDir, watchActive]) => {
                if (!watchDir || !watchActive) return;
                const currentServer = useAuthStore.getState().currentServer;
                if (!currentServer?.fbToken) return;
                ipc.invoke('sync:start-watch', {
                    filebrowserToken: currentServer.fbToken,
                    filebrowserUrl: urlConfig.filebrowser,
                    pymixUrl: urlConfig.pymix,
                    serverId: currentServer.id,
                    username: currentServer.username,
                    watchDir,
                }).catch(console.error);
            },
        );
    }, []);

    const [webAudio, setWebAudio] = useState<WebAudio>();

    useEffect(() => {
        if (enabled && content) {
            // Yes, CSS is sanitized here as well. Prevent a suer from changing the
            // localStorage to bypass sanitizing.
            const sanitized = sanitizeCss(content);
            if (!cssRef.current) {
                cssRef.current = document.createElement('style');
                document.body.appendChild(cssRef.current);
            }

            cssRef.current.textContent = sanitized;

            return () => {
                cssRef.current!.textContent = '';
            };
        }

        return () => {};
    }, [content, enabled]);

    const webAudioProvider = useMemo(() => {
        return { setWebAudio, webAudio };
    }, [webAudio]);

    useEffect(() => {
        if (isElectron()) {
            ipc?.send('set-global-shortcuts', bindings);
        }
    }, [bindings]);

    useEffect(() => {
        if (language) {
            i18n.changeLanguage(language);
        }
    }, [language]);

    useEffect(() => {
        if (isElectron()) {
            window.api.utils.rendererOpenSettings(() => {
                openSettingsModal();
            });

            return () => {
                ipc?.removeAllListeners('renderer-open-settings');
            };
        }
        return undefined;
    }, []);

    const notificationStyles = useMemo(
        () => ({
            root: {
                marginBottom: 90,
            },
        }),
        [],
    );

    return (
        <MantineProvider forceColorScheme={mode} theme={theme}>
            <Notifications
                containerWidth="300px"
                position="bottom-center"
                styles={notificationStyles}
                zIndex={50000}
            />
            <WebAudioContext.Provider value={webAudioProvider}>
                <PlayerProvider>
                    <AudioPlayers />
                    <AppRouter />
                </PlayerProvider>
            </WebAudioContext.Provider>
            {/* Mounted at the root because every leg of the invite funnel opens it — the
                landing page and auth modal (logged out) and the demo banner and blocked
                sync actions (logged in) live on opposite sides of the auth boundary. */}
            <RequestInviteModal />
            <Suspense fallback={null}>
                <UpdateAvailableDialog />
            </Suspense>
        </MantineProvider>
    );
};
